"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";
import { SlotService } from "@/lib/services/slotService";
import { BookingService } from "@/lib/services/bookingService";
import { UserService } from "@/lib/services/userService";
import type { Slot, ActiveBooking, BookingWithDetails } from "@/lib/supabase";
import {
  Calendar,
  ArrowLeft,
  Users,
  Clock,
  TicketIcon as Tickets,
  AlertTriangle,
  Info,
  Ban,
  RefreshCw,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";

export interface Booking {
  id: string;
  userId: string;
  status: "booked" | "cancelled" | "pending";
  createdAt: string;
  updatedAt?: string;
  slot: Slot;
}

interface Teammate {
  name: string;
  phone_number?: string;
}

const isSlotAvailable = (slot: Slot, currentUserId?: string): boolean => {
  if (slot.status === "blocked") return false;
  if (slot.status === "approved") return false;
  if (slot.booked_by_user_id && slot.booked_by_user_id !== currentUserId)
    return false;
  return true;
};

const BookingPage = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState<string>("");
  const [teammates, setTeammates] = useState<Teammate[]>([
    { name: "", phone_number: "" },
  ]);

  const [availableSlots, setAvailableSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasExistingBooking, setHasExistingBooking] = useState(false);
  const [existingBookingDetails, setExistingBookingDetails] =
    useState<ActiveBooking | null>(null);
  const [checkingDate, setCheckingDate] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated || user?.type !== "user") {
      navigate("/user-login");
      return;
    }
  }, [isAuthenticated, user, navigate, isLoading]);

  useEffect(() => {
    if (selectedDate && user) {
      checkExistingBookingForDate();
    } else {
      setHasExistingBooking(false);
      setExistingBookingDetails(null);
      setAvailableSlots([]);
      setSelectedSlot("");
    }
  }, [selectedDate, user]);

  // Add visibility change listener to refresh when user returns to tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && selectedDate && user) {
        // Refresh data when user returns to the page
        checkExistingBookingForDate();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [selectedDate, user]);

  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateString = minDate.toISOString().split("T")[0];

  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 7);
  const maxDateString = maxDate.toISOString().split("T")[0];

  const getTodayTomorrowText = (date: string) => {
    const today = new Date().toISOString().split("T")[0];
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowString = tomorrow.toISOString().split("T")[0];

    if (date === today) return "today";
    if (date === tomorrowString) return "tomorrow";
    return `on ${date}`;
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value >= minDateString && value <= maxDateString) {
      setSelectedDate(value);
    } else {
      alert(`Date must be between ${minDateString} and ${maxDateString}`);
      e.target.value = "";
    }
  };

  const checkExistingBookingForDate = async () => {
    if (!user || !selectedDate) return;

    setCheckingDate(true);

    try {
      const hasActiveBooking = await UserService.hasActiveBooking(user.id);
      setHasExistingBooking(hasActiveBooking);

      if (hasActiveBooking) {
        const bookingDetails = await UserService.getActiveBookingDetails(
          user.id
        );

        if (bookingDetails) {
          const activeBooking: ActiveBooking = {
            id: bookingDetails.id,
            status: bookingDetails.status,
            slot: {
              id: bookingDetails.slot.id,
              date: bookingDetails.slot.date,
              time_start: bookingDetails.slot.time_start,
              time_end: bookingDetails.slot.time_end,
              status: bookingDetails.slot.status ?? "pending",
              booked_by_user_id: bookingDetails.slot.booked_by_user_id ?? null,
              booking_id: bookingDetails.slot.booking_id ?? null,
              block_reason: bookingDetails.slot.block_reason ?? null,
            } as Slot,
          };

          setExistingBookingDetails(activeBooking);
          setSelectedSlot("");
          setAvailableSlots([]);

          const dateText = getTodayTomorrowText(activeBooking.slot.date);
          toast({
            title: "Active Booking Exists",
            description: `You have an active booking ${dateText} at ${formatTimeSlot(
              activeBooking.slot.time_start,
              activeBooking.slot.time_end
            )}. You must complete or cancel this booking before making a new one.`,
            variant: "destructive",
            duration: 5000,
          });
        }
      } else {
        loadAvailableSlots();
      }
    } catch (error) {
      console.error("Error checking existing booking:", error);
    } finally {
      setCheckingDate(false);
    }
  };

  const loadAvailableSlots = async () => {
    if (!selectedDate) return;

    setLoading(true);
    try {
      await SlotService.cleanupDuplicateSlots(selectedDate);
      // Clean up orphaned slots (slots with booked_by_user_id but no active booking)
      await SlotService.cleanupOrphanedSlots(selectedDate);
      
      const existingSlots = await SlotService.getAllSlotsForDate(selectedDate);

      if (existingSlots.length === 0) {
        await SlotService.createSlotsForDate(selectedDate);
      }

      // Use getAvailableSlots instead of getAllSlotsForDate to filter out booked slots
      const slots = await SlotService.getAvailableSlots(selectedDate);
      setAvailableSlots(slots);

      console.log(`Loaded ${slots.length} available slots for ${selectedDate}`);
    } catch (error) {
      console.error("Error loading slots:", error);
      toast({
        title: "Error",
        description: "Failed to load slots",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (!selectedDate || !user) return;

    setRefreshing(true);
    try {
      await checkExistingBookingForDate();
      toast({
        title: "Refreshed",
        description: "Slot availability updated",
      });
    } catch (error) {
      console.error("Error refreshing:", error);
      toast({
        title: "Error",
        description: "Failed to refresh data",
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
    }
  };

  const formatTimeSlot = (timeStart: string, timeEnd: string) => {
    const formatTime = (time: string) => {
      const [hours, minutes] = time.split(":");
      const hour = Number.parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minutes} ${ampm}`;
    };

    return `${formatTime(timeStart)} - ${formatTime(timeEnd)}`;
  };

  const addTeammate = () => {
    if (teammates.length < 12) {
      setTeammates([...teammates, { name: "", phone_number: "" }]);
    }
  };

  const updateTeammate = (
    index: number,
    field: "name" | "phone_number",
    value: string
  ) => {
    const updated = teammates.map((teammate, i) =>
      i === index ? { ...teammate, [field]: value } : teammate
    );
    setTeammates(updated);
  };

  const removeTeammate = (index: number) => {
    if (teammates.length > 1) {
      setTeammates(teammates.filter((_, i) => i !== index));
    }
  };

  const handleSlotToggle = (slotId: string) => {
    if (hasExistingBooking) {
      return;
    }

    if (selectedSlot === slotId) {
      setSelectedSlot("");
    } else {
      setSelectedSlot(slotId);
    }
  };

  const handleConfirmBooking = async () => {
    if (!user || user.type !== "user") return;

    const hasActiveBooking = await UserService.hasActiveBooking(user.id);
    if (hasActiveBooking) {
      toast({
        title: "Active Booking Exists",
        description:
          "You must complete or cancel your current booking before making a new one.",
        variant: "destructive",
      });
      return;
    }

    if (!selectedDate) {
      toast({
        title: "Select Date",
        description: "Please select a booking date",
        variant: "destructive",
      });
      return;
    }

    if (!selectedSlot) {
      toast({
        title: "Select Time Slot",
        description: "Please select a time slot",
        variant: "destructive",
      });
      return;
    }

    const validTeammates = teammates.filter(
      (t) =>
        t.name.trim() !== "" &&
        t.phone_number &&
        t.phone_number.trim() !== "" &&
        t.phone_number.length === 10
    );
    if (
      validTeammates.length === 0 ||
      validTeammates.length !== teammates.length
    ) {
      toast({
        title: "Add Team Members",
        description:
          "Please enter both name and a valid 10-digit mobile number for all team members.",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      const slotsBooked = await SlotService.bookSlots([selectedSlot], user.id);
      if (!slotsBooked) {
        toast({
          title: "Booking Failed",
          description:
            "This slot may have been booked by someone else. Please refresh and try again.",
          variant: "destructive",
        });
        return;
      }

      let bookingId: string | null;
      try {
        bookingId = await BookingService.createBooking(
          user.id,
          [selectedSlot],
          validTeammates
        );
        if (!bookingId) {
          toast({
            title: "Booking Failed",
            description: "Failed to create booking record. Please try again.",
            variant: "destructive",
          });
          return;
        }
      } catch (error: any) {
        // Handle user-friendly error messages
        const errorMessage = error?.message || "Failed to create booking record";
        toast({
          title: "Booking Failed",
          description: errorMessage,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Booking Confirmed!",
        description: `Your booking for ${selectedDate} has been submitted for admin approval.`,
      });

      navigate("/confirmation");
    } catch (error) {
      console.error("Booking error:", error);
      toast({
        title: "Booking Failed",
        description: "An error occurred while processing your booking",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/user-dashboard")}
              className="mr-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-blue-900">Book Turf Slot</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center">
                    <Calendar className="w-5 h-5 mr-2" />
                    Select Date
                  </CardTitle>
                  {selectedDate && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRefresh}
                      disabled={refreshing || checkingDate}
                    >
                      <RefreshCw
                        className={`w-4 h-4 mr-2 ${
                          refreshing ? "animate-spin" : ""
                        }`}
                      />
                      Refresh
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="date">
                    Booking Date (1 day advance, within next 7 days)
                  </Label>
                  <Input
                    id="date"
                    type="date"
                    min={minDateString}
                    max={maxDateString}
                    value={selectedDate}
                    onChange={handleDateChange}
                    className="cursor-pointer"
                  />

                  <p className="text-sm text-gray-500 mt-1">
                    You can book from {minDateString} to {maxDateString}
                  </p>
                </div>
                {checkingDate && (
                  <div className="text-center py-2">
                    <div className="inline-flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      <span className="text-sm text-gray-600">
                        Checking availability...
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {selectedDate && hasExistingBooking && existingBookingDetails && (
              <Alert className="border-red-200 bg-red-50">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">
                  <strong>Booking Already Exists!</strong>
                  <br />
                  You have already booked a slot{" "}
                  {getTodayTomorrowText(selectedDate)} at{" "}
                  {formatTimeSlot(
                    existingBookingDetails.slot.time_start,
                    existingBookingDetails.slot.time_end
                  )}{" "}
                  (<strong>{existingBookingDetails.status}</strong>).
                  <br />
                  <strong>
                    You can't book anything {getTodayTomorrowText(selectedDate)}
                    .
                  </strong>{" "}
                  Please select a different date to make a new booking.
                </AlertDescription>
              </Alert>
            )}

            {selectedDate && !hasExistingBooking && !checkingDate && (
              <Alert className="border-blue-200 bg-blue-50">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  <strong>Booking Rule:</strong> You can select only{" "}
                  <strong>ONE</strong> time slot per date. Once you book any
                  slot for {selectedDate}, you won't be able to book additional
                  slots for the same date.
                </AlertDescription>
              </Alert>
            )}

            {selectedDate && !hasExistingBooking && !checkingDate && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Clock className="w-5 h-5 mr-2" />
                    Available Time Slots
                  </CardTitle>
                  <CardDescription>
                    Select ONE time slot for {selectedDate}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="text-center py-4">Loading slots...</div>
                  ) : (
                    <div className="grid grid-cols-1 gap-3">
                      {availableSlots.map((slot) => {
                        const isBlocked = slot.status === "blocked";
                        const isApproved = slot.status === "approved";
                        const isPending =
                          slot.status === "pending" &&
                          slot.booked_by_user_id &&
                          slot.booked_by_user_id !== user?.id;
                        const isAvailable = isSlotAvailable(slot, user?.id);
                        const isSelected = selectedSlot === slot.id;
                        const isBookedByCurrentUser =
                          slot.booked_by_user_id === user?.id;

                        return (
                          <div
                            key={slot.id}
                            className={`flex items-center space-x-3 p-3 rounded-lg border ${
                              !isAvailable
                                ? "bg-gray-100 border-gray-300 opacity-60"
                                : isSelected
                                ? "bg-blue-50 border-blue-300 ring-2 ring-blue-200"
                                : "bg-white border-gray-200 hover:bg-gray-50"
                            }`}
                          >
                            <Checkbox
                              id={slot.id}
                              checked={isSelected}
                              disabled={!isAvailable}
                              onCheckedChange={() => handleSlotToggle(slot.id)}
                            />

                            <label
                              htmlFor={slot.id}
                              className="flex-1 cursor-pointer"
                            >
                              <div className="flex justify-between items-center">
                                <span
                                  className={`font-medium ${
                                    !isAvailable ? "text-gray-500" : ""
                                  }`}
                                >
                                  {formatTimeSlot(
                                    slot.time_start,
                                    slot.time_end
                                  )}
                                </span>
                                <span
                                  className={`text-sm ${
                                    isBlocked
                                      ? "text-red-500"
                                      : isApproved
                                      ? "text-red-500"
                                      : slot.booked_by_user_id &&
                                        slot.booked_by_user_id !== user?.id
                                      ? "text-orange-500"
                                      : isBookedByCurrentUser &&
                                        slot.status === "pending"
                                      ? "text-yellow-600"
                                      : isSelected
                                      ? "text-blue-600 font-medium"
                                      : "text-green-600"
                                  }`}
                                >
                                  {isBlocked
                                    ? "Blocked"
                                    : isApproved
                                    ? "Booked"
                                    : slot.booked_by_user_id &&
                                      slot.booked_by_user_id !== user?.id
                                    ? "Pending Booking"
                                    : isBookedByCurrentUser &&
                                      slot.status === "pending"
                                    ? "Pending Approval"
                                    : isSelected
                                    ? "Selected"
                                    : "Available"}
                                </span>
                              </div>
                              {slot.block_reason && (
                                <div className="text-xs text-gray-500 mt-1">
                                  Reason: {slot.block_reason}
                                </div>
                              )}
                            </label>
                          </div>
                        );
                      })}
                      {availableSlots.length === 0 && !loading && (
                        <div className="text-center py-4 text-gray-500">
                          No slots available for this date
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {selectedDate && hasExistingBooking && !checkingDate && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-gray-500">
                    <Ban className="w-5 h-5 mr-2" />
                    Time Slots - Disabled
                  </CardTitle>
                  <CardDescription className="text-red-600">
                    You already have a active booking
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <Ban className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <h3 className="text-lg font-medium text-gray-800 mb-1">
                      Active Booking Exists
                    </h3>
                    <div className="bg-white p-4 rounded-lg border w-full max-w-md mx-auto mb-4">
                      <h4 className="font-medium text-gray-800 mb-2">
                        Booking Rules
                      </h4>
                      <ul className="text-left text-gray-700 space-y-2 text-sm">
                        <li className="flex items-start">
                          <Info className="w-4 h-4 mr-2 mt-0.5 text-yellow-600 flex-shrink-0" />
                          <span>
                            You can only have one active booking at a time
                          </span>
                        </li>
                        <li className="flex items-start">
                          <Info className="w-4 h-4 mr-2 mt-0.5 text-yellow-600 flex-shrink-0" />
                          <span>
                            Once you complete this booking, you can book again
                          </span>
                        </li>
                        <li className="flex items-start">
                          <Info className="w-4 h-4 mr-2 mt-0.5 text-yellow-600 flex-shrink-0" />
                          <span>
                            You can cancel this booking if you want to book a
                            different slot
                          </span>
                        </li>
                      </ul>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => navigate("/booking-history")}
                      className="text-blue-600 border-blue-600 hover:bg-blue-50"
                    >
                      View Your Bookings
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  Team Members
                </CardTitle>
                <CardDescription>
                  Add up to 12 teammates (names and numbers are required)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {teammates.map((teammate, index) => (
                  <div key={index} className="flex space-x-2">
                    <div className="flex-1">
                      <Input
                        placeholder="Player name *"
                        value={teammate.name}
                        onChange={(e) => {
                          const lettersOnly = e.target.value.replace(
                            /[^a-zA-Z\s]/g,
                            ""
                          );
                          updateTeammate(index, "name", lettersOnly);
                        }}
                        disabled={hasExistingBooking}
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        placeholder="Mobile *"
                        value={teammate.phone_number}
                        onChange={(e) => {
                          const input = e.target.value.replace(/\D/g, "");
                          if (input.length <= 10) {
                            updateTeammate(index, "phone_number", input);
                          }
                        }}
                        disabled={hasExistingBooking}
                      />
                    </div>
                    {teammates.length > 1 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeTeammate(index)}
                        disabled={hasExistingBooking}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}

                {teammates.length < 12 && (
                  <Button
                    variant="outline"
                    onClick={addTeammate}
                    className="w-full"
                    disabled={hasExistingBooking}
                  >
                    Add Team Member
                  </Button>
                )}
              </CardContent>
            </Card>

            {selectedSlot && !hasExistingBooking && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Tickets className="w-5 h-5 mr-2" />
                    Booking Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Date:</span>
                      <span className="font-medium">{selectedDate}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Time Slot:</span>
                      <span className="font-medium">
                        {availableSlots.find(
                          (slot) => slot.id === selectedSlot
                        ) &&
                          formatTimeSlot(
                            availableSlots.find(
                              (slot) => slot.id === selectedSlot
                            )!.time_start,
                            availableSlots.find(
                              (slot) => slot.id === selectedSlot
                            )!.time_end
                          )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Team Size:</span>
                      <span className="font-medium">
                        {teammates.filter((t) => t.name.trim()).length}
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={handleConfirmBooking}
                    className="w-full mt-4 bg-green-600 hover:bg-green-700"
                    disabled={submitting || hasExistingBooking}
                  >
                    {submitting ? "Confirming..." : "Confirm Your Slot"}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default BookingPage;
